import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where, doc, getDoc, limit, orderBy, updateDoc, getCountFromServer, Timestamp } from "firebase/firestore";
import moment from "moment";
import {
  Card, CircularProgress, Avatar, Box, Typography, Divider,
  Stack, Grid, IconButton, Button, Chip
} from "@mui/material";
import {
  School, People, DirectionsBus, Hotel,
  ArrowForwardIos, AccountBalanceWallet, Refresh, Warning,
  LocalMall, Commute, MoreHoriz, Check
} from "@mui/icons-material";
import { WhatsApp } from "@mui/icons-material";
import { LinearProgress } from "@mui/material";

const PRIMARY = "#0d9488";
const SECONDARY = "#64748b";
const BG = "#f1f5f9";
const DARK = "#0f172a";
const COLORS = ["#0d9488", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

function Dashboard() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState({ total: 0, sent: 0, remaining: 0 });
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestEnquiries, setGuestEnquiries] = useState([]);
  const [recentPasses, setRecentPasses] = useState([]);
  const [paymentLogs, setPaymentLogs] = useState([]);
  const [checkingLogId, setCheckingLogId] = useState(null);
  const [todayCollection, setTodayCollection] = useState(0);
  const [hostelStats, setHostelStats] = useState([]);
  const [summary, setSummary] = useState({
    students: 0,
    teachers: 0,
    staff: 0,
    classes: 0,
    vehicles: 0,
    hostelers: 0,
    activePasses: 0
  });

  const [collections, setCollections] = useState({
    total: 0,
    wallet: 0,
    dressStationary: 0,
    transport: 0,
    other: 0,
    otherHead: 0,
    inventoryTotal: 0,
  });

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        setUserData(userDoc.exists() ? userDoc.data() : user);
      } else {
        navigate("/login");
      }
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if (!userData?.schoolId) return;

    const fetchData1 = async () => {
      try {
        const sid = userData.schoolId;
        const queries = [
          getDocs(query(collection(db, "students"), where("schoolId", "==", sid))),
          getDocs(query(collection(db, "users"), where("schoolId", "==", sid), where("role", "==", "Teacher"))),
          getDocs(query(collection(db, "users"), where("schoolId", "==", sid), where("role", "==", "Staff"))),
          getDocs(query(collection(db, "courses"), where("schoolId", "==", sid))),
          getDocs(query(collection(db, "vehicles"), where("schoolId", "==", sid))),
          getDocs(query(collection(db, "admission"), where("schoolId", "==", sid), where("studenttype", "==", "Boarding"))),
          getDocs(query(collection(db, "guestEnquiries"), orderBy("createdAt", "desc"), limit(4))),
          getDocs(query(collection(db, "fee_heads"), where("schoolId", "==", sid))),
        ];

        const [sS, tS, stS, cS, vS, hS, gS, feeHeadsSnap] = await Promise.all(queries);

        let maleCount = 0;
        let femaleCount = 0;
        sS.forEach((doc) => {
          const gender = (doc.data().gender || "").toUpperCase();
          if (gender === "M" || gender === "MALE") maleCount++;
          else if (gender === "F" || gender === "FEMALE") femaleCount++;
        });

        setSummary({
          students: sS.size || 0,
          maleStudents: maleCount,
          femaleStudents: femaleCount,
          teachers: tS.size || 0,
          staff: stS.size || 0,
          classes: cS.size || 0,
          vehicles: vS.size || 0,
          hostelers: hS.size || 0,
        });

        setGuestEnquiries(gS.docs.map(d => ({ id: d.id, ...d.data() })));

        const feeHeadMap = {};
        feeHeadsSnap.forEach((d) => {
          feeHeadMap[d.id] = (d.data().name || "").toLowerCase().trim();
        });

        let totalMessages = 0;
        let sentCount = 0;
        try {
          const walletDoc = await getDoc(doc(db, "wallets", "0YVnAJmiq8cajkf4ufE5a8xYoFQ2"));
          totalMessages = walletDoc.exists() ? (walletDoc.data().totalMessages || 0) : 0;
          const historySnapshot = await getCountFromServer(query(collection(db, "whatsapphistory")));
          sentCount = historySnapshot.data().count || 0;
        } catch (err) {
          console.log("Wallet fetch skipped", err);
        }

        setWallet({
          total: totalMessages,
          sent: sentCount,
          remaining: totalMessages - sentCount
        });

        const todayStr = moment().format('YYYY-MM-DD');
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);

        const isToday = (dateValue) => {
          if (!dateValue) return false;
          if (typeof dateValue === 'string') {
            if (dateValue === todayStr) return true;
            const parsedDate = new Date(dateValue);
            return parsedDate >= startOfDay && parsedDate <= endOfDay;
          }
          const parsedDate = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
          return parsedDate >= startOfDay && parsedDate <= endOfDay;
        };

        const feeQuery = query(collection(db, "fee_structures"), where("schoolId", "==", sid));
        const feeSnapshot = await getDocs(feeQuery);

        let grandTotal = 0;
        let dressStatAmt = 0;
        let transportAmt = 0;
        let otherAmtHead = 0;
        let otherAmt = 0;
        let inventoryTotal = 0;

        feeSnapshot.forEach(docSnap => {
          const payments = docSnap.data().paymentHistory || [];
          payments.forEach(p => {
            if (isToday(p.date)) {
              const totalAmt = Number(p.paidAmount || 0);
              grandTotal += totalAmt;

              if (p.multiHeads && p.multiHeads.length > 0) {
                p.multiHeads.forEach(mh => {
                  const mhAmount = Number(mh.paidAmount || 0);
                  const headName = feeHeadMap[mh.headId] || (p.note || "").toLowerCase().trim();

                  if (headName.includes("577d80a0") || headName.includes("stationary") || headName.includes("stationery") || headName.includes("book") || headName.includes("uniform")) {
                    dressStatAmt += mhAmount;
                  } else if (headName.includes("9da5fe5b") || headName.includes("bus") || headName.includes("van") || headName.includes("vehicle")) {
                    transportAmt += mhAmount;
                  } else {
                    otherAmtHead += mhAmount;
                  }
                });
              } else {
                const headName = feeHeadMap[p.headId] || (p.note || "").toLowerCase().trim();

                if (headName.includes("577d80a0") || headName.includes("stationary") || headName.includes("stationery") || headName.includes("book") || headName.includes("uniform")) {
                  dressStatAmt += totalAmt;
                } else if (headName.includes("9da5fe5b") || headName.includes("bus") || headName.includes("van") || headName.includes("vehicle")) {
                  transportAmt += totalAmt;
                } else {
                  otherAmtHead += totalAmt;
                }
              }
            }
          });
        });

        const inventoryQuery = query(collection(db, "studentInventory"), where("payMode", "!=", "Wallet"));
        const inventorySnapshot = await getDocs(inventoryQuery);
        inventorySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (isToday(data.issueDate || data.date)) {
            inventoryTotal += Number(data.grandTotal || data.amount || 0);
          }
        });

        const incomeQuery = query(collection(db, "income"), where("schoolId", "==", sid));
        const incomeSnap = await getDocs(incomeQuery);
        let generalIncomeTotal = 0;

        incomeSnap.forEach((docSnap) => {
          const data = docSnap.data();
          if (isToday(data.date)) {
            generalIncomeTotal += Number(data.amount || 0);
          }
        });

        otherAmt += generalIncomeTotal;
        grandTotal += generalIncomeTotal;

        const walletTxnQuery = query(
          collection(db, "wallet_transactions"),
          where("schoolId", "==", sid),
          where("type", "==", "Credit"),
          where("timestamp", ">=", Timestamp.fromDate(startOfDay)),
          where("timestamp", "<=", Timestamp.fromDate(endOfDay))
        );
        const walletTxnSnap = await getDocs(walletTxnQuery);
        let walletTxnTotal = 0;
        walletTxnSnap.forEach((doc) => {
          walletTxnTotal += Number(doc.data().amount || 0);
        });

        setCollections({
          total: grandTotal + inventoryTotal + walletTxnTotal,
          wallet: walletTxnTotal,
          dressStationary: dressStatAmt,
          transport: transportAmt,
          otherHead: otherAmtHead,
          other: otherAmt,
          generalIncome: generalIncomeTotal,
          inventoryTotal: inventoryTotal,
        });

        const hostelsSnap = await getDocs(query(collection(db, "hostels"), where("schoolId", "==", sid)));
        const hostelsMap = {};
        hostelsSnap.forEach(d => { hostelsMap[d.id] = d.data().hostelName; });

        const allocsSnap = await getDocs(query(collection(db, "roomallocations"), where("schoolId", "==", sid), where("status", "==", "Live")));
        const hTotals = {};
        allocsSnap.forEach(d => {
          const hid = d.data().hostelId;
          if (hid) hTotals[hid] = (hTotals[hid] || 0) + 1;
        });

        const attSnap = await getDocs(query(collection(db, "hostel_attendance"), where("schoolId", "==", sid), where("date", "==", todayStr)));
        const latestAtt = {};
        attSnap.forEach(d => {
          const data = d.data();
          const hid = data.hostelId;
          if (hid) {
            if (!latestAtt[hid] || (latestAtt[hid].updatedAt?.seconds < data.updatedAt?.seconds)) {
              latestAtt[hid] = data;
            }
          }
        });

        const hStats = Object.keys(hostelsMap).map(hid => {
          const total = hTotals[hid] || 0;
          let present = 0;
          if (latestAtt[hid] && latestAtt[hid].records) {
            present = Object.values(latestAtt[hid].records).filter(r => r.status === "Present").length;
          }
          return {
            id: hid,
            name: hostelsMap[hid],
            total,
            present
          };
        });
        setHostelStats(hStats);

      } catch (err) {
        console.error("Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData1();
  }, [userData]);

  useEffect(() => {
    if (!userData?.schoolId) return;

    const fetchData = async () => {
      try {
        const sid = userData.schoolId;
        const queries = [
          getDocs(query(collection(db, "admission"), where("schoolId", "==", sid), where('status', '==', 'Active'))),
          getDocs(query(collection(db, "users"), where("schoolId", "==", sid), where("role", "==", "Teacher"))),
          getDocs(query(collection(db, "users"), where("schoolId", "==", sid), where("role", "==", "Staff"))),
          getDocs(query(collection(db, "courses"), where("schoolId", "==", sid))),
          getDocs(query(collection(db, "vehicles"), where("schoolId", "==", sid))),
          getDocs(query(collection(db, "admission"), where("schoolId", "==", sid), where("studenttype", "==", "Boarding"), where('status', '==', 'Active'))),
          getDocs(query(collection(db, "guestEnquiries"), orderBy("createdAt", "desc"), limit(4))),
          getDocs(query(collection(db, "gate_passes"), where("schoolId", "==", sid), where("status", "==", "Pending"))),
          getDocs(query(collection(db, "payment_logs"), where("status", "in", ["FAILED_OR_CANCELLED", "INITIATED", "INIT_ERROR", "GATEWAY_SUCCESS"])))
        ];

        const [sS, tS, stS, cS, vS, hS, gS, pS, logS] = await Promise.all(queries);

        setSummary(prev => ({
          ...prev,
          students: sS.size,
          teachers: tS.size,
          staff: stS.size,
          classes: cS.size,
          vehicles: vS.size,
          hostelers: hS.size,
          activePasses: pS ? pS.size : 0
        }));

        setGuestEnquiries(gS.docs.map(d => ({ id: d.id, ...d.data() })));
        setRecentPasses(pS.docs.slice(0, 4).map(d => ({ id: d.id, ...d.data() })));

        const logsData = [];
        for (const logDoc of logS.docs) {
          const logData = { id: logDoc.id, ...logDoc.data() };
          let studentName = "Unknown Student";

          if (logData.studentId || logData.admissionId) {
            const targetId = logData.admissionId || logData.studentId;
            try {
              const admDocRef = doc(db, "students", targetId);
              const admSnap = await getDoc(admDocRef);
              if (admSnap.exists()) {
                const admInfo = admSnap.data();
                studentName = `${admInfo.firstname || ""} ${admInfo.fathername || ""} ${admInfo.surname || ""}`.trim() || "Unknown Student";
              }
            } catch (e) {
              console.error("Error fetching student details for log:", e);
            }
          }
          logsData.push({ ...logData, studentName });
        }
        setPaymentLogs(logsData);

        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);
        const todayString = new Date().toISOString().split('T')[0];

        const feeQuery = query(collection(db, "fee_structures"), where("schoolId", "==", sid));
        const incomeQuery = query(collection(db, "income"), where("schoolId", "==", sid));
        const inventoryQuery = query(collection(db, "studentInventory"), where("payMode", "!=", "Wallet"));
        const walletQuery = query(collection(db, "wallet_transactions"), where("schoolId", "==", sid));

        const hospitalQuery = query(
          collection(db, "hospitalBills"),
          where("schoolId", "==", sid),
          where("payMode", "!=", "Wallet")
        );

        const [feeSnapshot, incomeSnapshot, inventorySnapshot, walletSnapshot, hospitalSnapshot] = await Promise.all([
          getDocs(feeQuery),
          getDocs(incomeQuery),
          getDocs(inventoryQuery),
          getDocs(walletQuery),
          getDocs(hospitalQuery)
        ]);

        let grandTotal = 0;

        const isToday = (dateValue) => {
          if (!dateValue) return false;
          if (typeof dateValue === 'string') {
            if (dateValue === todayString) return true;
            const parsedDate = new Date(dateValue);
            return parsedDate >= startOfDay && parsedDate <= endOfDay;
          }
          const parsedDate = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
          return parsedDate >= startOfDay && parsedDate <= endOfDay;
        };

        feeSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const payments = data.paymentHistory || [];
          payments.forEach((p) => {
            if (isToday(p.date)) {
              grandTotal += Number(p.paidAmount || 0);
            }
          });
        });

        incomeSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (isToday(data.date)) {
            grandTotal += Number(data.amount || 0);
          }
        });

        inventorySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (isToday(data.issueDate || data.date)) {
            grandTotal += Number(data.grandTotal || data.amount || 0);
          }
        });

        walletSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const isCredit = data.type === 'Credit' || data.type === 'deposit';
          if (isCredit && isToday(data.dateTime || data.date)) {
            grandTotal += Number(data.amount || 0);
          }
        });

        hospitalSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (isToday(data.paidDate || data.date)) {
            grandTotal += Number(data.paidAmount || 0);
          }
        });

        setTodayCollection(grandTotal);

      } catch (err) {
        console.error("Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userData]);

  const usagePercent = wallet.total > 0 ? (wallet.sent / wallet.total) * 100 : 0;

  const handleMarkDone = async (log) => {
    setCheckingLogId(log.id);
    try {
      await updateDoc(doc(db, "payment_logs", log.id), {
        status: "RESOLVED",
        updatedAt: new Date().toISOString()
      });
      setPaymentLogs(prev => prev.filter(item => item.id !== log.id));
    } catch (error) {
      console.error("Error updating log status:", error);
      alert("Failed to mark as done. Please try again.");
    } finally {
      setCheckingLogId(null);
    }
  };

  const collectionCategories = [
    { label: "Wallet", amount: collections.wallet, icon: <AccountBalanceWallet sx={{ fontSize: 18 }} />, color: "#3b82f6", bg: "#eff6ff" },
    { label: "Dress & Stationery", amount: collections.dressStationary, icon: <LocalMall sx={{ fontSize: 18 }} />, color: "#8b5cf6", bg: "#f5f3ff" },
    { label: "Transport", amount: collections.transport, icon: <Commute sx={{ fontSize: 18 }} />, color: "#f59e0b", bg: "#fffbeb" },
    { label: "Other Income", amount: collections.other, icon: <MoreHoriz sx={{ fontSize: 18 }} />, color: "#10b981", bg: "#ecfdf5" },
    { label: "Other Heads", amount: collections.otherHead, icon: <MoreHoriz sx={{ fontSize: 18 }} />, color: "#b91089", bg: "#eceffd" },
    { label: "Student Inventory", amount: collections.inventoryTotal, icon: <MoreHoriz sx={{ fontSize: 18 }} />, color: "#e33f3f", bg: "#fdecfb" },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center', background: BG }}>
        <CircularProgress style={{ color: PRIMARY }} />
      </Box>
    );
  }

  const statCards = [
    { title: "Total Students", val: summary.students, color: "#0d9488", icon: <School />, path: "/students" },
    { title: "Active Teachers", val: summary.teachers, color: "#3b82f6", icon: <People />, path: "/teachers" },
    { title: "Fleet Size", val: summary.vehicles, color: "#f59e0b", icon: <DirectionsBus />, path: "/vehicles" },
    { title: "Hostel", val: summary.hostelers, color: "#ef4444", icon: <Hotel />, path: "/hostel" },
  ];

  const chartData = [
    { name: "Day Scholars", value: summary.students - summary.hostelers },
    { name: "Hostelers", value: summary.hostelers },
  ];

  return (
    <Box sx={{ background: BG, minHeight: "100vh", p: 1 }}>
      <Grid className="row" spacing={3}>
        <Grid item className="col-sm-12 mb-2">
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}  spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={900} color={DARK} sx={{ letterSpacing: '-0.5px' }}>
                Dashboard
              </Typography>
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} className="col-sm-4 mb-2">
          <Grid className="row" spacing={2}>
            <Grid item className="col-sm-12 mb-2">
              <Card sx={{
                p: 2,
                borderRadius: 4,
                border: '1px solid #e2e8f0',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                color: '#fff'
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8, fontWeight: 700 }}>
                      Today's Collection
                    </Typography>
                    <Typography variant="h4" fontWeight={800}>
                      ₹ {todayCollection.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Fee Collected Today
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }}>
                    <AccountBalanceWallet />
                  </Avatar>
                </Stack>
              </Card>
            </Grid>
            <Grid item className="col-sm-12 mb-2">
              <Card sx={{ p: 2, borderRadius: 4, border: '1px solid #e2e8f0', background: 'linear-gradient(135deg, #075E54 0%, #128C7E 100%)', color: '#fff' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8, fontWeight: 700 }}>WhatsApp Wallet</Typography>
                    <Typography variant="h4" fontWeight={800}>{wallet.remaining.toLocaleString()}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Credits Available</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }}>
                    <WhatsApp />
                  </Avatar>
                </Stack>

                <Box sx={{ mt: 3 }}>
                  <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Typography variant="caption" fontWeight={600}>Usage: {wallet.sent} / {wallet.total}</Typography>
                    <Typography variant="caption" fontWeight={600}>{Math.round(usagePercent)}%</Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={usagePercent}
                    sx={{ height: 8, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#25D366' } }}
                  />
                </Box>
              </Card>
            </Grid>
          </Grid>

          <Grid className="row" spacing={2} sx={{ mt: 1 }}>
            {statCards.map((card, i) => (
              <Grid className="col-sm-6 mb-2" item xs={6} sm={6} key={i}>
                <Card
                  onClick={() => navigate(card.path)}
                  sx={{
                    p: 3, borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" fontWeight={700} color={SECONDARY} sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>{card.title}</Typography>
                      <Typography variant="h4" fontWeight={800} color={DARK}>{card.val}</Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: `${card.color}15`, color: card.color, width: 52, height: 52, borderRadius: 3 }}>
                      {card.icon}
                    </Avatar>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={12} className="col-sm-4 mb-2">
          <Stack spacing={3}>
            <Card sx={{ p: 2.5, borderRadius: 4, border: '1px solid #e2e8f0' }}>
              <Grid className="row" spacing={1.5}>
                {collectionCategories.map((cat, idx) => (
                  <Grid className="col-sm-12 mb-2" item xs={12} key={idx}>
                    <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: cat.bg, border: '1px solid #e2e8f0', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.01)' } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{ width: 32, height: 32, bgcolor: cat.color, color: '#fff' }}>
                            {cat.icon}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={700} color={DARK}>{cat.label}</Typography>
                            <Typography variant="caption" color={SECONDARY}>Daily Revenue</Typography>
                          </Box>
                        </Stack>
                        <Typography variant="subtitle1" fontWeight={800} color={DARK}>
                          ₹ {(cat.amount ?? 0).toLocaleString()}
                        </Typography>
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Stack>
        </Grid>

        {/* RIGHT COLUMN: Payment Exceptions, Pending Passes, Campus Overview */}
        <Grid item xs={12} className="col-sm-4 mb-2">
          <Stack spacing={3}>
            <Card sx={{ p: 2, borderRadius: 4, border: '1px solid #e2e8f0' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={800} color={DARK}>Payment Exception Logs</Typography>
                  <Typography variant="caption" color={SECONDARY}>Review failed or interrupted online payments</Typography>
                </Box>
                <Chip label={`${paymentLogs.length} Pending`} color="warning" size="small" sx={{ fontWeight: 700 }} />
              </Stack>

              {paymentLogs.length === 0 ? (
                <Typography variant="body2" color={SECONDARY} textAlign="center" py={4}>No unresolved payment logs found.</Typography>
              ) : (
                <Stack spacing={2}>
                  {paymentLogs.map((log) => (
                    <Box key={log.id} sx={{ p: 2, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                          <Warning color="error" sx={{ fontSize: 18 }} />
                          <Typography variant="body2" fontWeight={700} color={DARK}>{log.studentName} </Typography>
                          <Chip label={log.status} size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                        </Stack>
                        <Typography variant="caption" color={SECONDARY} display="block">Order ID: {log.razorpay_order_id || 'N/A'} • Amount: ₹{log.amount}</Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={checkingLogId === log.id}
                        startIcon={checkingLogId === log.id ? <CircularProgress size={14} color="inherit" /> : <Check />}
                        onClick={() => handleMarkDone(log)}
                        sx={{ bgcolor: PRIMARY, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                      >
                        {checkingLogId === log.id ? "Updating..." : "Done"}
                      </Button>
                    </Box>
                  ))}
                </Stack>
              )}
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;